import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { toast } from 'sonner';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';

import { paths } from 'src/routes/paths';

import { API_URL } from 'src/config';

import {
  getTenantIdLS,
  getAccessTokenLS,
  removeTenantIdLS,
  setLastApiCallLS,
  removeAccessTokenLS,
  removeLastApiCallLS,
} from './localStorageUtils';

type CustomFetchBaseQueryError = FetchBaseQueryError & {
  originalStatus?: number;
};

let sessionExpiredNotified = false; // Prevent duplicate session expiration messages

const AUTH_ENDPOINTS = [
  'loginUser',
  'registerUser',
  'verifyEmail',
  'verifyCode',
  'resetPwd',
  'autoLoginUser',
  'completeSsoOnboarding',
];

// Endpoints that use FormData — don't set Content-Type (browser sets multipart boundary)
const FORM_DATA_ENDPOINTS = ['uploadKBDocument', 'createWhatsappTemplate'];

const queryHeaders = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const accessToken = getAccessTokenLS();
    const state = getState() as any;
    const persistRoot = localStorage.getItem('persist:root');

    // Skip session-expired check for auth-related endpoints
    if (AUTH_ENDPOINTS.includes(endpoint)) {
      return headers;
    }

    // If no persistRoot and no auth state, session might be expired
    if (!persistRoot && !state.auth?.loginApiData) {
      if (!sessionExpiredNotified) {
        sessionExpiredNotified = true;

        window.postMessage({ type: 'DISCONNECT_SIP' }, '*');
        window.postMessage({ type: 'DISCONNECT_SOCKET' }, '*');

        removeAccessTokenLS();
        removeTenantIdLS();
        removeLastApiCallLS();
        localStorage.removeItem('persist:root');
        toast.error('Session expired. Please log in again.');
        setTimeout(() => {
          sessionExpiredNotified = false;
          window.location.href = paths.auth.signIn;
        }, 1500);
      }

      return headers;
    }

    let loginDtl = state.auth?.loginApiData;

    // Fallback to parsing persistRoot if state.auth is not available
    if (!loginDtl && persistRoot) {
      try {
        const userPersistData = JSON.parse(persistRoot);
        loginDtl = JSON.parse(userPersistData.auth)?.loginApiData;
      } catch (err) {
        console.error('Failed to parse login data:', err);

        if (!sessionExpiredNotified) {
          sessionExpiredNotified = true;
          toast.error('Corrupted session. Please log in again.');
          window.postMessage({ type: 'DISCONNECT_SIP' }, '*');
          window.postMessage({ type: 'DISCONNECT_SOCKET' }, '*');
          removeAccessTokenLS();
          removeTenantIdLS();
          removeLastApiCallLS();
          localStorage.removeItem('persist:root');

          setTimeout(() => {
            sessionExpiredNotified = false;
            window.location.href = paths.auth.signIn;
          }, 1500);
        }

        return headers;
      }
    }

    const selectedTenantRecord = getTenantIdLS();

    if (accessToken) {
      headers.set('Authorization', `${accessToken}`);
    }

    const tenantId = selectedTenantRecord || loginDtl?.tenantId || loginDtl?.tenant?.id;

    if (tenantId) {
      headers.set('tenantId', tenantId.toString());
      headers.set('x-tenant-id', tenantId.toString());
    }

    // Don't set Content-Type for FormData uploads (browser sets multipart boundary)
    if (!FORM_DATA_ENDPOINTS.includes(endpoint)) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});

const baseQuery = async (args: string | FetchArgs, api: any, extraOptions: any) => {
  if (!AUTH_ENDPOINTS.includes(api.endpoint)) {
    const accessToken = getAccessTokenLS();
    if (!accessToken || (typeof window !== 'undefined' && (window as any).isLoggingOut)) {
      return { data: {} };
    }
  }

  setLastApiCallLS(Date.now().toString());
  const result = await queryHeaders(args, api, extraOptions);
  const error = result.error as CustomFetchBaseQueryError;

  const errorMessage =
    error && error.data && typeof error.data === 'object' && 'message' in error.data
      ? (error.data as { message?: string }).message
      : undefined;

  if (
    (error?.originalStatus === 401 || error?.status === 401) &&
    !sessionExpiredNotified &&
    !AUTH_ENDPOINTS.includes(api.endpoint)
  ) {
    sessionExpiredNotified = true;

    if (errorMessage === 'Tenant is not ACTIVE' || errorMessage === 'User is not ACTIVE') {
      toast.error('Tenant/User is not active.');
    } else {
      toast.error('Session expired. Please log in again.');
    }

    window.postMessage({ type: 'DISCONNECT_SIP' }, '*');
    window.postMessage({ type: 'DISCONNECT_SOCKET' }, '*');
    removeAccessTokenLS();
    removeTenantIdLS();
    removeLastApiCallLS();
    localStorage.removeItem('persist:root');

    setTimeout(() => {
      sessionExpiredNotified = false;
      window.location.href = paths.auth.signIn;
    }, 1500);
  }

  return result;
};

export default baseQuery;
